/**
 * The shell's one read of what this person opens on this business (vsidebar-b).
 *
 * `my_vertical_access` is asked once a session per business, and only where it can
 * answer anything but "everything": a business with at least one industry desk switched
 * on, read by someone who is not its owner or admin. Everyone else costs no call.
 */
import { supabase } from '@/db/supabase';
import { useAuth } from '@/shared/hooks/useAuth';
import { useOrgQuery } from '@/shared/hooks/useOrgQuery';
import { useModules } from '@/shared/hooks/useModules';
import { accessFrom, OPEN_ALL, VERTICALS, type VerticalAccess } from './verticalAccess';

export async function readVerticalAccess(orgId: string): Promise<VerticalAccess> {
    const { data, error } = await supabase.rpc('my_vertical_access', { p_org_id: orgId });
    if (error) throw error;
    return accessFrom(data);
}

/** Owners and admins open everything; no read is made for them. */
export function roleOpensEverything(role: string | null | undefined): boolean {
    return role === 'owner' || role === 'admin';
}

/**
 * This person's access on the open business, or null while it is not known (not read yet,
 * or a read that failed): null hides nothing, and the server still refuses what it must.
 */
export function useVerticalAccess(): VerticalAccess | null {
    const role = useAuth((s) => (s.currentOrg as { role?: string } | null)?.role ?? null);
    const { isActive } = useModules();
    const anyVertical = VERTICALS.some((v) => v.modules.some((m) => isActive(m)));
    const enabled = anyVertical && !roleOpensEverything(role);
    const q = useOrgQuery(['vertical-access'], readVerticalAccess, { enabled, staleTime: Infinity, live: false });
    if (!enabled) return OPEN_ALL;
    return q.data ?? null;
}
